import { useRef, useState, useEffect, useMemo, useCallback } from 'react'

import { InfiniteData } from '@tanstack/react-query'

import {
  PaginatedSpace,
  QuerySpacesArgs,
} from '@tribeplatform/gql-client/types'
import { useSpaces } from '@tribeplatform/react-sdk/hooks'

import { DEFAULT_TREE_ITEM_LIMIT } from '../constants.js'

export function useCollectionSpaces({
  variables,
  enabled,
}: {
  variables: Omit<QuerySpacesArgs, 'limit'>
  enabled: boolean
}) {
  const previousSpaces = useRef<InfiniteData<PaginatedSpace>>()
  const [limit, setLimit] = useState(DEFAULT_TREE_ITEM_LIMIT)

  const {
    data: spaces,
    isLoading,
    hasNextPage,
    isRefetching,
  } = useSpaces({
    fields: {
      image: 'basic',
    },
    variables: {
      ...variables,
      limit,
    },
    useInfiniteQueryOptions: {
      enabled,
      placeholderData: previousSpaces.current,
    },
  })

  useEffect(() => {
    if (spaces) {
      previousSpaces.current = spaces
    }
  }, [spaces])

  const items = useMemo(() => {
    return spaces?.pages.flatMap(p => p.nodes) ?? []
  }, [spaces])

  const loadRest = useCallback(() => {
    const total = spaces?.pages.at(0)?.totalCount ?? 0
    if (total > limit) {
      setLimit(total)
    }
  }, [limit, spaces])

  return {
    items,
    isLoading: isLoading || (limit > DEFAULT_TREE_ITEM_LIMIT && isRefetching),
    loadRest,
    loadedAll: !hasNextPage,
  }
}
